import { useQuery, useMutation, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  CreateLmsSettingsTemplateRequest,
  LmsSettingsTemplateDetail,
  LmsSettingsTemplateListResponse,
  LmsSettingsTemplateMutationResult,
} from './live-settings-templates-types';

// ============================================
// Query keys
// ============================================

export const liveSettingsTemplateKeys = {
  all: ['livestream', 'settings_templates'] as const,
  list: (serviceId: string) =>
    ['livestream', 'settings_templates', serviceId] as const,
  detail: (serviceId: string, id: number) =>
    ['livestream', 'settings_template_detail', serviceId, { id }] as const,
};

// ============================================
// Queries
// ============================================

export function useLmsSettingsTemplates(
  serviceId: string,
  options?: Omit<UseQueryOptions<LmsSettingsTemplateListResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LmsSettingsTemplateListResponse>({
    queryKey: liveSettingsTemplateKeys.list(serviceId),
    enabled: !!serviceId,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/domains`);
      const body = res.data ?? {};
      return {
        items: body?.data ?? [],
        total: body?.total ?? body?.data?.length ?? 0,
      };
    },
  });
}

export function useLmsSettingsTemplateDetail(
  serviceId: string,
  id: number,
  options?: Omit<UseQueryOptions<LmsSettingsTemplateDetail>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LmsSettingsTemplateDetail>({
    queryKey: liveSettingsTemplateKeys.detail(serviceId, id),
    enabled: !!serviceId && !!id,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/domains/${id}`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateLmsSettingsTemplate(
  serviceId: string,
  options?: UseMutationOptions<LmsSettingsTemplateMutationResult, Error, CreateLmsSettingsTemplateRequest>,
) {
  const qc = useQueryClient();
  return useMutation<LmsSettingsTemplateMutationResult, Error, CreateLmsSettingsTemplateRequest>({
    mutationFn: async (payload: CreateLmsSettingsTemplateRequest) => {
      const res = await serviceApiV3.post(`${mediaBase(serviceId, 'streams')}/domains`, payload);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveSettingsTemplateKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateLmsSettingsTemplate(
  serviceId: string,
  options?: UseMutationOptions<LmsSettingsTemplateMutationResult, Error, { id: number; payload: CreateLmsSettingsTemplateRequest }>,
) {
  const qc = useQueryClient();
  return useMutation<LmsSettingsTemplateMutationResult, Error, { id: number; payload: CreateLmsSettingsTemplateRequest }>({
    mutationFn: async ({ id, payload }) => {
      const res = await serviceApiV3.put(`${mediaBase(serviceId, 'streams')}/domains/${id}`, payload);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveSettingsTemplateKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteLmsSettingsTemplate(
  serviceId: string,
  options?: UseMutationOptions<void, Error, number>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      await serviceApiV3.delete(`${mediaBase(serviceId, 'streams')}/domains/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveSettingsTemplateKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
